import { useCallback } from 'react';
import { Card, Button, Form, Alert, ProgressBar, Badge } from 'react-bootstrap';
import { FaUpload, FaFileUpload, FaInfoCircle, FaTimes, FaFilePdf, FaFileImage, FaFileWord } from 'react-icons/fa';

const TransactionDocuments = ({
  documents,
  selectedType,
  onFileUpload,
  onRemoveDocument,
  requiredDocuments
}) => {
  const fileInputId = 'file-upload';

  const handleFileSelect = () => {
    document.getElementById(fileInputId).click();
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileUpload(files);
    }
  }, [onFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FaFilePdf className="text-danger me-2" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <FaFileImage className="text-success me-2" />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord className="text-primary me-2" />;
    return <FaFileUpload className="text-muted me-2" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="step-content">
      {/* Guide des documents */}
      <Alert variant={selectedType?.requires.documents ? 'warning' : 'info'} className="mb-4">
        <FaInfoCircle className="me-2" />
        <div>
          <strong>Documents justificatifs</strong>
          <div className="mt-2">
            {selectedType?.requires.documents ? (
              <>
                <p className="mb-2">
                  Pour ce type de transaction, des documents sont <strong>obligatoires</strong> pour la validation.
                </p>
                {selectedType.value === 'Emission' && (
                  <ul className="mb-0">
                    <li>Prospectus d'émission approuvé</li>
                    <li>Procès-verbal de l'assemblée générale</li>
                    <li>Attestation de dépôt AMF</li>
                  </ul>
                )}
                {selectedType.value === 'Mutation' && (
                  <ul className="mb-0">
                    <li>Acte de mutation notarié</li>
                    <li>Certificat de décès (le cas échéant)</li>
                    <li>Pièce d'identité du bénéficiaire</li>
                  </ul>
                )}
                {selectedType.value === 'Sequestre' && (
                  <ul className="mb-0">
                    <li>Décision de justice datée et signée</li>
                    <li>Ordonnance de séquestre</li>
                    <li>Pièce d'identité du requérant</li>
                  </ul>
                )}
              </>
            ) : (
              <p className="mb-0">
                Les documents sont <strong>recommandés</strong> pour la traçabilité et l'audit.
                Vous pouvez ajouter des contrats, des décisions ou toute autre pièce justificative.
              </p>
            )}
          </div>
        </div>
      </Alert>

      {/* Zone de dépôt */}
      <Card 
        className="border-dashed mb-4"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Card.Body className="text-center p-5">
          <FaUpload className="text-muted mb-3" size={48} />
          <h5 className="mb-2">Déposez vos documents</h5>
          <p className="text-muted mb-3">
            Glissez-déposez vos fichiers ici ou cliquez pour les sélectionner
          </p>
          
          <Form.Control
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            onChange={(e) => onFileUpload(e.target.files)}
            className="d-none"
            id={fileInputId}
          />
          
          <Button 
            variant="outline-primary" 
            onClick={handleFileSelect}
            className="px-4"
          >
            <FaFileUpload className="me-2" />
            Sélectionner des fichiers
          </Button>
          
          <div className="mt-3">
            <small className="d-block text-muted">
              Formats acceptés : PDF, Images (JPG, PNG), Documents Word/Excel
            </small>
            <small className="d-block text-muted">
              Taille maximale : 10MB par fichier
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Liste des documents */}
      {documents.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              Documents ajoutés 
              <Badge bg="primary" className="ms-2">
                {documents.length}
              </Badge>
            </h6>
            <small className="text-muted">
              Total : {formatFileSize(documents.reduce((total, file) => total + file.size, 0))}
            </small>
          </div>
          
          <div className="list-group">
            {documents.map((file, index) => (
              <div 
                key={index} 
                className="list-group-item d-flex justify-content-between align-items-center file-list-item"
              >
                <div className="d-flex align-items-center flex-grow-1">
                  {getFileIcon(file.name)}
                  <div className="flex-grow-1">
                    <div className="fw-medium text-truncate">{file.name}</div>
                    <div className="d-flex align-items-center mt-1">
                      <small className="text-muted me-3">{formatFileSize(file.size)}</small>
                      <small className="text-muted">
                        Type: {file.type || 'Non spécifié'}
                      </small>
                    </div>
                    {file.size > 10 * 1024 * 1024 && (
                      <div className="mt-1">
                        <small className="text-danger">
                          ⚠️ Fichier trop volumineux
                        </small>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  onClick={() => onRemoveDocument(index)}
                  className="ms-3"
                >
                  <FaTimes />
                </Button>
              </div>
            ))}
          </div>

          {/* Barre de progression */}
          {documents.some(file => file.size > 10 * 1024 * 1024) && (
            <Alert variant="danger" className="mt-3">
              <FaInfoCircle className="me-2" />
              Certains fichiers dépassent la limite de 10MB. Veuillez les compresser ou les remplacer.
            </Alert>
          )}
        </div>
      )}

      {/* Indicateur de documents requis */}
      {selectedType?.requires.documents && documents.length === 0 && (
        <Alert variant="warning" className="mt-3">
          <FaInfoCircle className="me-2" />
          <strong>Attention :</strong> Des documents sont requis pour ce type de transaction.
          Votre transaction pourrait être rejetée sans les documents justificatifs appropriés.
        </Alert>
      )}

      {/* Conseils */}
      <Card className="mt-4 border-light">
        <Card.Body className="p-3">
          <small className="text-muted d-flex align-items-center">
            <FaInfoCircle className="me-2 flex-shrink-0" />
            <span>
              <strong>Conseil :</strong> Numérisez vos documents en haute qualité (300 DPI) et assurez-vous
              qu'ils sont lisibles avant de les téléverser. Les documents doivent être signés et datés.
            </span>
          </small>
        </Card.Body>
      </Card>
    </div>
  );
};

// Liste des documents requis par type de transaction
TransactionDocuments.defaultProps = {
  requiredDocuments: {
    'Emission': ['Prospectus', 'PV AG'],
    'Mutation': ['Acte mutation', 'Certificat décès'],
    'Sequestre': ['Décision justice'],
    'Conversion': ['Demande conversion'],
    'CessionOnereuse': ['Contrat cession'],
    'TransfertDossiers': ['Convention transfert']
  }
};

export default TransactionDocuments;